if (!customElements.get('swatch-overflow')) {
  class SwatchOverflow extends HTMLElement {
    static SELECTORS = {
      swatch: ':scope > [data-td-swatch-item]',
      toggle: ':scope > [data-td-swatch-toggle]',
    };
    static CLASSES = {
      hidden: 'td-product-color-variants__item--hidden',
    };
    static VISIBLE_ROWS = 2;

    #resizeObserver = null;
    #rafId = null;
    #expanded = false;
    #swatches = [];
    #toggle = null;

    connectedCallback() {
      this.#swatches = Array.from(this.querySelectorAll(SwatchOverflow.SELECTORS.swatch));
      this.#toggle = this.querySelector(SwatchOverflow.SELECTORS.toggle);

      if (this.closest('.quick-add-modal, .quick-add-modal__content')) {
        this.#initQuickAddMode();
        return;
      }

      this.#toggle?.addEventListener('click', this.#handleToggleClick);

      this.#resizeObserver = new ResizeObserver(() => this.#queueSync());
      this.#resizeObserver.observe(this);
      this.#queueSync();
    }

    #initQuickAddMode() {
      this.#swatches.forEach((swatch) => swatch.classList.remove(SwatchOverflow.CLASSES.hidden));

      if (this.#toggle) {
        this.#toggle.hidden = true;
      }

      this.setAttribute('data-initialized', '');
    }

    disconnectedCallback() {
      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;
      this.#toggle?.removeEventListener('click', this.#handleToggleClick);
      if (this.#rafId) cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }

    #queueSync() {
      if (this.#rafId) return;
      this.#rafId = requestAnimationFrame(() => {
        this.#rafId = null;
        this.#sync();
      });
    }

    #sync() {
      this.#swatches.forEach((swatch) => swatch.classList.remove(SwatchOverflow.CLASSES.hidden));
      if (this.#toggle) this.#toggle.hidden = true;
      this.setAttribute('data-initialized', '');

      if (this.#expanded || !this.#toggle || this.#swatches.length === 0) {
        this.#toggle?.setAttribute('aria-expanded', String(this.#expanded));
        return;
      }

      this.appendChild(this.#toggle);
      this.#toggle.hidden = false;
      this.#toggle.setAttribute('aria-expanded', 'false');

      const centerOf = (el) => {
        const r = el.getBoundingClientRect();
        return Math.round(r.top + r.height / 2);
      };

      const ROW_TOLERANCE = 2;
      const allItems = [...this.#swatches, this.#toggle];
      const centers = allItems.map(centerOf);
      const rowCenters = [];
      centers.forEach((center) => {
        if (!rowCenters.some((rc) => Math.abs(rc - center) <= ROW_TOLERANCE)) {
          rowCenters.push(center);
        }
      });
      rowCenters.sort((a, b) => a - b);

      if (rowCenters.length <= SwatchOverflow.VISIBLE_ROWS) {
        this.#toggle.hidden = true;
        return;
      }

      const lastRowCenter = rowCenters[SwatchOverflow.VISIBLE_ROWS - 1];
      const rowOf = (index) =>
        rowCenters.findIndex((rc) => Math.abs(rc - centers[index]) <= ROW_TOLERANCE);

      this.#swatches.forEach((swatch, index) => {
        if (rowOf(index) > SwatchOverflow.VISIBLE_ROWS - 1) {
          swatch.classList.add(SwatchOverflow.CLASSES.hidden);
        }
      });

      const lastRowSwatchIndices = this.#swatches
        .map((_, index) => index)
        .filter(
          (index) =>
            rowOf(index) === SwatchOverflow.VISIBLE_ROWS - 1 &&
            !this.#swatches[index].classList.contains(SwatchOverflow.CLASSES.hidden)
        );

      for (let i = lastRowSwatchIndices.length - 1; i >= 0; i -= 1) {
        if (centerOf(this.#toggle) <= lastRowCenter + ROW_TOLERANCE) break;
        this.#swatches[lastRowSwatchIndices[i]].classList.add(SwatchOverflow.CLASSES.hidden);
      }
    }

    #handleToggleClick = () => {
      this.#expanded = true;
      this.#sync();
    };
  }

  customElements.define('swatch-overflow', SwatchOverflow);
}
